import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

const tableStyles = {
  tableContainer: {
    position: 'relative',
    overflow: 'auto',
    maxHeight: '75vh',
  },
  table: {
    tableLayout: 'fixed',
    minWidth: '1200px',
  },
  cellConstraints: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stickyActions: {
    position: 'sticky',
    right: 0,
    backgroundColor: '#ffffff',
    borderLeft: '2px solid #dee2e6',
    boxShadow: '-3px 0 6px rgba(0,0,0,0.08)',
    zIndex: 10,
  },
  actionButton: {
    fontSize: '12px',
    padding: '6px 10px',
    minWidth: '34px',
    height: '32px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
  },
  actionIcon: {
    fontSize: '13px',
  },
};

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = {
  serial: true,
  title: true,
  client: true,
  platform: true,
  // campaign_type: true,
  assigned_to: true,
  due_date: true,
  status: true,
  priority: true,
  actions: true,
};

// Confirmation modal for delete action
const ConfirmDeleteModal = ({ show, onHide, onConfirm }) => {
  if (!show) return null;
  return (
    <div
      className='modal fade show d-block'
      tabIndex='-1'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <i className='fa fa-exclamation-triangle text-danger me-2'></i>
              Confirm Delete
            </h5>
            <button
              type='button'
              className='btn-close'
              onClick={onHide}
            ></button>
          </div>
          <div className='modal-body'>
            <p>
              Are you sure you want to delete this ads activity? This action
              cannot be undone.
            </p>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={onHide}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={onConfirm}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdsActivitiesTableView = ({
  activities,
  onDelete,
  deleteLoading,
  pagination,
  onPageChange,
  loading,
}) => {
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Load saved column preferences from localStorage
  useEffect(() => {
    const savedColumns = localStorage.getItem('ads-activity-table-columns');
    if (savedColumns) {
      try {
        setVisibleColumns(JSON.parse(savedColumns));
      } catch (error) {
        console.error('Error loading saved columns:', error);
      }
    }
  }, []);

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge bg-warning text-dark fw-medium px-2 py-1',
      in_progress: 'badge bg-primary text-white fw-medium px-2 py-1',
      completed: 'badge bg-success text-white fw-medium px-2 py-1',
      on_hold: 'badge bg-secondary text-white fw-medium px-2 py-1',
      cancelled: 'badge bg-danger text-white fw-medium px-2 py-1',
    };
    return (
      statusClasses[status] || 'badge bg-light text-dark fw-medium px-2 py-1'
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'badge bg-light text-dark fw-medium px-2 py-1',
      medium: 'badge bg-warning text-dark fw-medium px-2 py-1',
      high: 'badge bg-danger text-white fw-medium px-2 py-1',
      urgent: 'badge bg-dark text-white fw-medium px-2 py-1',
    };
    return (
      priorityClasses[priority] ||
      'badge bg-light text-dark fw-medium px-2 py-1'
    );
  };

  // Function to get row background color based on status and overdue state
  const getRowBackgroundColor = (activity) => {
    if (activity.status === 'COMPLETED') {
      return 'table-success-subtle'; // Very light green for completed
    }
    if (activity.status === 'CANCELLED') {
      return 'table-danger-subtle'; // Very light red for cancelled
    }
    if (activity.status === 'ON_HOLD') {
      return 'table-secondary-subtle'; // Very light gray for on hold
    }
    if (
      moment(activity.due_date).isBefore(moment()) &&
      activity.status !== 'COMPLETED' &&
      activity.status !== 'CANCELLED'
    ) {
      return 'table-warning-subtle'; // Very light yellow for overdue
    }
    if (activity.status === 'IN_PROGRESS') {
      return 'table-primary-subtle'; // Very light blue for in progress
    }
    if (activity.status === 'PENDING') {
      return 'table-warning-subtle'; // Very light yellow for pending
    }
    return ''; // Default white background
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      google_ads: 'fab fa-google text-primary',
      facebook_ads: 'fab fa-facebook text-primary',
      instagram_ads: 'fab fa-instagram text-danger',
      linkedin_ads: 'fab fa-linkedin text-info',
      twitter_ads: 'fab fa-twitter text-info',
      tiktok_ads: 'fab fa-tiktok text-dark',
    };
    return platformIcons[platform] || 'fas fa-globe text-muted';
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      onDelete(deleteId);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  if (loading) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '300px' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='container-fluid'>
      <div className='card shadow-sm border-0'>
        {/* Table Header */}
        <div className='card-header bg-white border-bottom d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center'>
            <h5 className='mb-0 text-dark'>
              <i className='fas fa-bullhorn me-2'></i>
              Ads Activities Management
            </h5>
          </div>
          <div className='d-flex align-items-center'>
            <span className='badge bg-primary me-2'>
              Total: {pagination?.totalItems || 0}
            </span>
            <Link
              to='/ads-activities/create'
              className='btn btn-primary btn-sm'
            >
              <i className='fas fa-plus me-1'></i>
              Add New
            </Link>
          </div>
        </div>

        {/* Table Content */}
        <div className='card-body p-0'>
          <div style={tableStyles.tableContainer}>
            <table className='table table-hover mb-0' style={tableStyles.table}>
              <thead className='table-light sticky-top'>
                <tr>
                  {visibleColumns.serial && (
                    <th scope='col' style={{ width: '80px' }}>
                      <i className='fa fa-hashtag me-1'></i>
                      Serial
                    </th>
                  )}
                  {visibleColumns.title && (
                    <th scope='col' style={{ width: '250px' }}>
                      <i className='fa fa-tasks me-1'></i>
                      Title
                    </th>
                  )}
                  {visibleColumns.client && (
                    <th scope='col' style={{ width: '150px' }}>
                      <i className='fa fa-building me-1'></i>
                      Client
                    </th>
                  )}
                  {visibleColumns.platform && (
                    <th scope='col' style={{ width: '120px' }}>
                      <i className='fa fa-globe me-1'></i>
                      Platform
                    </th>
                  )}
                  {/* {visibleColumns.campaign_type && (
                    <th scope='col' style={{ width: '150px' }}>
                      <i className='fa fa-bullhorn me-1'></i>
                      Campaign Type
                    </th>
                  )} */}
                  {visibleColumns.assigned_to && (
                    <th scope='col' style={{ width: '150px' }}>
                      <i className='fa fa-user me-1'></i>
                      Assigned To
                    </th>
                  )}
                  {visibleColumns.due_date && (
                    <th scope='col' style={{ width: '120px' }}>
                      <i className='fa fa-calendar me-1'></i>
                      Due Date
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th scope='col' style={{ width: '120px' }}>
                      <i className='fa fa-flag me-1'></i>
                      Status
                    </th>
                  )}
                  {visibleColumns.priority && (
                    <th scope='col' style={{ width: '100px' }}>
                      <i className='fa fa-exclamation-triangle me-1'></i>
                      Priority
                    </th>
                  )}
                  {visibleColumns.actions && (
                    <th
                      scope='col'
                      style={{ ...tableStyles.stickyActions, width: '90px' }}
                    >
                      {/* <i className='fa fa-ellipsis-h me-1'></i> */}
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {activities.length === 0 ? (
                  <tr>
                    <td colSpan='10' className='text-center py-5'>
                      <i className='fas fa-clipboard-list fa-3x text-muted mb-3'></i>
                      <div className='h5 text-muted'>
                        No ads activities found
                      </div>
                      <p className='text-muted'>
                        Create your first ads activity to get started
                      </p>
                    </td>
                  </tr>
                ) : (
                  activities.map((activity, index) => (
                    <tr
                      key={activity._id}
                      className={`align-middle ${getRowBackgroundColor(
                        activity
                      )}`}
                    >
                      {visibleColumns.serial && (
                        <td style={tableStyles.cellConstraints}>
                          <span className='fw-semibold text-muted'>
                            {((pagination?.currentPage || 1) - 1) *
                              (pagination?.limit || 10) +
                              index +
                              1}
                          </span>
                        </td>
                      )}
                      {visibleColumns.title && (
                        <td style={tableStyles.cellConstraints}>
                          <div
                            className='fw-semibold text-dark'
                            title={activity.title}
                          >
                            {activity.title}
                          </div>
                          {activity.description && (
                            <small
                              className='text-muted'
                              title={activity.description}
                            >
                              {activity.description.length > 50
                                ? activity.description.substring(0, 50) + '...'
                                : activity.description}
                            </small>
                          )}
                        </td>
                      )}
                      {visibleColumns.client && (
                        <td style={tableStyles.cellConstraints}>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-building text-muted me-2'></i>
                            <span title={activity.client?.name || 'No client'}>
                              {activity.client?.name || 'No client'}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.platform && (
                        <td style={tableStyles.cellConstraints}>
                          <div className='d-flex align-items-center'>
                            <i
                              className={`${getPlatformIcon(
                                activity.platform
                              )} me-2`}
                            ></i>
                            <span className='text-capitalize'>
                              {activity.platform?.replace('_', ' ') || 'N/A'}
                            </span>
                          </div>
                        </td>
                      )}
                      {/* {visibleColumns.campaign_type && (
                        <td style={tableStyles.cellConstraints}>
                          <span className='text-capitalize'>
                            {activity.campaign_type?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td>
                      )} */}
                      {visibleColumns.assigned_to && (
                        <td style={tableStyles.cellConstraints}>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-user text-muted me-2'></i>
                            <span
                              title={activity.assigned_to?.name || 'Unassigned'}
                            >
                              {activity.assigned_to?.name || 'Unassigned'}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.due_date && (
                        <td style={tableStyles.cellConstraints}>
                          {activity.due_date ? (
                            <div>
                              <div className='small'>
                                {moment(activity.due_date).format(
                                  'MMM DD, YYYY'
                                )}
                              </div>
                              <div
                                className={`small ${
                                  moment(activity.due_date).isBefore(
                                    moment()
                                  ) &&
                                  activity.status !== 'COMPLETED' &&
                                  activity.status !== 'CANCELLED'
                                    ? 'text-danger'
                                    : 'text-muted'
                                }`}
                              >
                                {moment(activity.due_date).isBefore(moment()) &&
                                activity.status !== 'COMPLETED' &&
                                activity.status !== 'CANCELLED'
                                  ? 'Overdue'
                                  : moment(activity.due_date).fromNow()}
                              </div>
                            </div>
                          ) : (
                            <span className='text-muted'>No due date</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td style={tableStyles.cellConstraints}>
                          <span className={getStatusBadge(activity.status)}>
                            {activity.status?.replace('_', ' ').toUpperCase() ||
                              'PENDING'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.priority && (
                        <td style={tableStyles.cellConstraints}>
                          <span className={getPriorityBadge(activity.priority)}>
                            {activity.priority?.toUpperCase() || 'LOW'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.actions && (
                        <td style={tableStyles.stickyActions}>
                          <div className='d-flex gap-1'>
                            <Link
                              to={`/ads-activities/${activity._id}`}
                              className='btn btn-outline-primary btn-sm'
                              style={tableStyles.actionButton}
                              title='View Details'
                            >
                              <i
                                className='fa fa-eye'
                                style={tableStyles.actionIcon}
                              ></i>
                            </Link>
                            {/* <Link
                              to={`/ads-activities/${activity._id}/edit`}
                              className='btn btn-outline-secondary btn-sm'
                              style={tableStyles.actionButton}
                              title='Edit'
                            >
                              <i
                                className='fa fa-edit'
                                style={tableStyles.actionIcon}
                              ></i>
                            </Link> */}
                            {/* <button
                              className='btn btn-outline-danger btn-sm'
                              style={tableStyles.actionButton}
                              onClick={() => handleDeleteClick(activity._id)}
                              disabled={deleteLoading}
                              title='Delete'
                            >
                              <i
                                className='fa fa-trash'
                                style={tableStyles.actionIcon}
                              ></i>
                            </button> */}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Footer */}
        {pagination && pagination.totalPages > 1 && (
          <div className='card-footer bg-white border-top'>
            <div className='d-flex justify-content-between align-items-center'>
              <div className='text-muted small'>
                Showing{' '}
                {((pagination?.currentPage || 1) - 1) *
                  (pagination?.limit || 10) +
                  1}{' '}
                to{' '}
                {Math.min(
                  (pagination?.currentPage || 1) * (pagination?.limit || 10),
                  pagination?.totalItems || 0
                )}{' '}
                of {pagination?.totalItems || 0} entries
              </div>
              <nav>
                <ul className='pagination pagination-sm mb-0'>
                  <li
                    className={`page-item ${
                      (pagination?.currentPage || 1) === 1 ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() =>
                        onPageChange((pagination?.currentPage || 1) - 1)
                      }
                      disabled={(pagination?.currentPage || 1) === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(pagination?.totalPages || 1)].map((_, i) => (
                    <li
                      key={i + 1}
                      className={`page-item ${
                        (pagination?.currentPage || 1) === i + 1 ? 'active' : ''
                      }`}
                    >
                      <button
                        className='page-link'
                        onClick={() => onPageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      (pagination?.currentPage || 1) ===
                      (pagination?.totalPages || 1)
                        ? 'disabled'
                        : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() =>
                        onPageChange((pagination?.currentPage || 1) + 1)
                      }
                      disabled={
                        (pagination?.currentPage || 1) ===
                        (pagination?.totalPages || 1)
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Custom CSS for very subtle table row colors and enhanced badges */}
      <style jsx>{`
        .table-success-subtle {
          background-color: rgba(25, 135, 84, 0.03) !important;
        }

        .table-danger-subtle {
          background-color: rgba(220, 53, 69, 0.03) !important;
        }

        .table-secondary-subtle {
          background-color: rgba(108, 117, 125, 0.03) !important;
        }

        .table-warning-subtle {
          background-color: rgba(255, 193, 7, 0.03) !important;
        }

        .table-primary-subtle {
          background-color: rgba(13, 110, 253, 0.03) !important;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }

        .badge {
          border-radius: 6px !important;
          font-size: 0.75rem !important;
          letter-spacing: 0.5px !important;
          padding: 0.4em 0.8em !important;
          font-weight: 500 !important;
        }

        .badge.fw-medium {
          font-weight: 500 !important;
        }

        .badge.px-2 {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }

        .badge.py-1 {
          padding-top: 0.4em !important;
          padding-bottom: 0.4em !important;
        }
      `}</style>
    </div>
  );
};

export default AdsActivitiesTableView;
